import { useEffect, useMemo } from 'react';
import { upperFirst } from 'es-toolkit/string';
import { logger } from '@zep/core-logger';
import { type UserVoucherRes, UserVoucherResDisplayStatus } from '@/api/gen/_model';
import { useGetMyVouchers } from '@/api/gen/vouchers/vouchers.gen';
import { EnterTracking } from '@/components/EnterTracking';
import { isProduction } from '@/consts/env';
import { type VoucherItem } from './components/type';
import { formatDateByTimezone } from './components/utils';
import { VoucherStorageSection, VoucherSummarySection } from './components';
const mapToVoucherItem = (voucher: UserVoucherRes): VoucherItem => ({
  id: voucher.hashId,
  status: voucher.displayStatus,
  name: voucher.campaignName || '',
  code: voucher.code || '',
  planDuration: `${voucher.durationDays}`,
  planType: upperFirst(voucher.planType.toLowerCase()),
  expirationDate: formatDateByTimezone(voucher.expiryDate, voucher.timezone || 'Asia/Seoul'),
  canUseNow: voucher.canUseNow,
  timezone: voucher.timezone
});
const countVouchersByStatus = (vouchers: VoucherItem[]) => {
  const counts = {
    total_owned_cnt: vouchers.length,
    in_use_cnt: 0,
    available_cnt: 0,
    used_cnt: 0,
    expired_cnt: 0,
    deactivated_cnt: 0
  };
  vouchers.forEach(voucher => {
    switch (voucher.status) {
      case UserVoucherResDisplayStatus.IN_USE:
        counts.in_use_cnt++;
        break;
      case UserVoucherResDisplayStatus.UNUSED:
        counts.available_cnt++;
        break;
      case UserVoucherResDisplayStatus.USED:
        counts.used_cnt++;
        break;
      case UserVoucherResDisplayStatus.EXPIRED:
        counts.expired_cnt++;
        break;
      case UserVoucherResDisplayStatus.DEACTIVATED:
        counts.deactivated_cnt++;
        break;
    }
  });
  return counts;
};
export const VoucherContainer = () => {
  const {
    data,
    isLoading: isFetchingVouchers,
    isError,
    error
  } = useGetMyVouchers();
  useEffect(() => {
    if (isProduction && isError) {
      logger.captureError(error, {
        tags: {
          feature: 'voucher',
          action: 'fetchVouchers'
        }
      });
    }
  }, [isError, error]);
  const vouchersData = data?.data;
  const vouchers = useMemo(() => {
    return (vouchersData?.items || []).map(mapToVoucherItem);
  }, [vouchersData?.items]);
  const voucherCountsByStatus = countVouchersByStatus(vouchers);
  return <EnterTracking eventName="enter_voucher_page" eventProperties={{
    ...voucherCountsByStatus
  }} isReady={!isFetchingVouchers && vouchersData !== undefined} data-sentry-element="EnterTracking" data-sentry-component="VoucherContainer" data-sentry-source-file="VoucherContainer.tsx">
      <div className="flex w-full max-w-[1840px] flex-col gap-md px-none mobile-lg:gap-lg">
        {/* 사용 가능한 이용권 + 이용권 등록 */}
        <VoucherSummarySection data-sentry-element="VoucherSummarySection" data-sentry-source-file="VoucherContainer.tsx" />

        {/* 이용권 보관함 */}
        <VoucherStorageSection vouchers={vouchers} canUseVouchers={vouchersData?.canUseVouchers || false} isLoading={isFetchingVouchers} data-sentry-element="VoucherStorageSection" data-sentry-source-file="VoucherContainer.tsx" />
      </div>
    </EnterTracking>;
};